import { CloseCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { RcFile, UploadFile } from 'antd/lib/upload';
import Dragger from 'antd/lib/upload/Dragger';
import React, { memo, useState } from 'react';
import { reportError } from '@xbcb/ui-utils';
import { importInvoicesSpreadsheet } from 'libs/invoiceHelpers';
import {
  StyledContainer,
  StyledDiv,
  StyledHeader,
  StyledParagraph,
  StyledUploadButton,
  StyledUploadDragContainer,
} from './styles';
import { AdditionalTariffSpreadsheet } from 'libs/htsHelpers';

export interface ImportAdditionalTariffUploadProps {
  fileList: UploadFile[];
  isLoading: boolean;
  additionalTariffSpreadsheets?: AdditionalTariffSpreadsheet[];
  setFileList: (fileList: UploadFile[]) => void;
  setIsLoading: (isLoading: boolean) => void;
  setAdditionalTariffSpreadsheets: (
    spreadsheets: AdditionalTariffSpreadsheet[] | undefined,
  ) => void;
  setIsPreviewReady: (isLoading: boolean) => void;
}

const acceptedFileTypes = ['.csv', '.xls', '.xlsx'];

/**
 * ImportAdditionalTariffUpload
 *
 * A sub-component of the ImportAdditionalTariffModal that contains the upload
 * feature and the duplicate handling settings.
 */

export const ImportAdditionalTariffUpload: React.FC<
  ImportAdditionalTariffUploadProps
> = ({
  fileList,
  isLoading,
  setFileList,
  setIsLoading,
  setAdditionalTariffSpreadsheets,
  setIsPreviewReady,
}) => {
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);

  const beforeUpload = async (file: RcFile) => {
    setIsLoading(true);
    try {
      const spreadsheets = await importInvoicesSpreadsheet(file);
      setAdditionalTariffSpreadsheets(spreadsheets);
      setIsPreviewReady(true);
    } catch (e) {
      reportError(e instanceof Error ? e : new Error(String(e)));
      setUploadErrors([e.message]);
      setIsPreviewReady(false);
    }
    setIsLoading(false);
    return false;
  };

  const handleChange = (info: { fileList: UploadFile[] }) => {
    setFileList(info.fileList.slice(-1));
  };

  const handleRemove = () => {
    setAdditionalTariffSpreadsheets(undefined);
    setIsPreviewReady(false);
  };

  return (
    <StyledContainer>
      <StyledDiv>
        <StyledHeader>File to import</StyledHeader>
        <Dragger
          data-testid={'upload-tariff-dragger'}
          accept={acceptedFileTypes.join(',')}
          beforeUpload={beforeUpload}
          disabled={isLoading}
          fileList={fileList}
          onChange={handleChange}
          onRemove={handleRemove}
        >
          <StyledUploadDragContainer>
            <StyledUploadButton icon={<UploadOutlined />} type="primary">
              Upload
            </StyledUploadButton>
            <p>Or drag and drop file here to upload</p>
          </StyledUploadDragContainer>
        </Dragger>
      </StyledDiv>
      {uploadErrors.length > 0 ? (
        <StyledDiv>
          <StyledHeader>Error parsing uploaded file:</StyledHeader>
          {uploadErrors.map((error, index) => (
            <StyledParagraph key={index}>
              <CloseCircleOutlined />
              {error}
            </StyledParagraph>
          ))}
        </StyledDiv>
      ) : null}
    </StyledContainer>
  );
};

export default memo(ImportAdditionalTariffUpload);
